import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DatePicker, ConfigProvider } from 'antd';
import dayjs from 'dayjs';
import zhTW from 'antd/locale/zh_TW';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import { useLanguage } from '../../contexts/LanguageContext';
import WpaLayout from './WpaLayout';
import { formatWpaCommerceApiError } from './wpaCommerceFetch';
import { formatWpaDate, getWpaCompanyTimezone } from './wpaDateUtils';
import useWpaDeliveryAvailability from './wpaDeliveryAvailability';
import {
  computeLineAmountFromRow,
  getProductQtyStepUnitLabel,
  shouldLockWpaUnitStepOfOne,
  resolveFastQtyStepSize,
  formatQtyStepForDisplay,
  collectMoqViolations,
  buildMoqSubmitErrorMessage,
} from './wpaProductUtils';

const PRIMARY = '#4CAF50';
/** 訂單明細數量列：與品名／價錢列協調（已提交僅顯示「數量＋單位」時勿過大） */
const QTY_CTRL_PX = 16;
const QTY_CTRL_LH = 1.25;
const QTY_CTRL_BTN = 36;
const QTY_CTRL_FONT_FAMILY =
  'system-ui, -apple-system, "Segoe UI", Roboto, "PingFang TC", "Microsoft JhengHei", sans-serif';

const styles = {
  loading: {
    textAlign: 'center',
    padding: 48,
    color: '#999',
  },
  card: {
    margin: 16,
    padding: 16,
    background: '#fff',
    borderRadius: 10,
    border: '1px solid #e0e0e0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#888',
    marginBottom: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  fieldRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid #f5f5f5',
  },
  fieldLabel: {
    fontSize: 14,
    color: '#666',
  },
  fieldValue: {
    fontSize: 14,
    fontWeight: 500,
    color: '#333',
    textAlign: 'right',
    maxWidth: '60%',
    wordBreak: 'break-word',
  },
  input: {
    fontSize: 14,
    padding: '8px 10px',
    border: '1px solid #ddd',
    borderRadius: 6,
    width: '60%',
    textAlign: 'right',
    outline: 'none',
  },
  inputAddress: {
    fontSize: 14,
    padding: '8px 10px',
    border: '1px solid #ddd',
    borderRadius: 6,
    width: '60%',
    minHeight: 72,
    lineHeight: 1.4,
    resize: 'vertical',
    boxSizing: 'border-box',
    textAlign: 'left',
    outline: 'none',
  },
  fieldRowAddress: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '8px 0',
    borderBottom: '1px solid #f5f5f5',
  },
  lineItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: '1px solid #f0f0f0',
    gap: 8,
  },
  lineInfo: {
    flex: 1,
    minWidth: 0,
  },
  lineName: {
    fontSize: 14,
    fontWeight: 500,
    color: '#333',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  lineItemNo: {
    fontSize: 12,
    color: '#999',
    marginTop: 2,
  },
  linePrice: {
    fontSize: 14,
    fontWeight: 600,
    color: PRIMARY,
    marginTop: 2,
  },
  lineSubtotal: {
    fontSize: 13,
    color: '#666',
    marginTop: 2,
  },
  qtyRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  /** 數量列：按鈕與中間欄同字級，尺寸適中 */
  qtyBtn: {
    width: QTY_CTRL_BTN,
    minWidth: QTY_CTRL_BTN,
    minHeight: QTY_CTRL_BTN,
    height: QTY_CTRL_BTN,
    borderRadius: 8,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#111',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    boxSizing: 'border-box',
    margin: 0,
    WebkitAppearance: 'none',
    appearance: 'none',
    font: `600 ${QTY_CTRL_PX}px/${QTY_CTRL_LH} ${QTY_CTRL_FONT_FAMILY}`,
    WebkitTextSizeAdjust: '100%',
  },
  qtyBtnWide: {
    minWidth: 56,
    width: 'auto',
    padding: '0 8px',
  },
  qtyText: {
    color: '#111',
    fontSize: QTY_CTRL_PX,
    fontWeight: 600,
    lineHeight: QTY_CTRL_LH,
    fontFamily: QTY_CTRL_FONT_FAMILY,
    minWidth: 36,
    padding: '0 6px',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
  },
  lineDeleteBtn: {
    background: 'none',
    border: 'none',
    fontSize: QTY_CTRL_PX,
    fontWeight: 600,
    lineHeight: QTY_CTRL_LH,
    fontFamily: QTY_CTRL_FONT_FAMILY,
    color: '#e53935',
    cursor: 'pointer',
    padding: 4,
    flexShrink: 0,
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 0 4px',
    marginTop: 8,
  },
  totalLabel: {
    fontSize: 16,
    fontWeight: 600,
    color: '#333',
  },
  totalValue: {
    fontSize: 20,
    fontWeight: 700,
    color: PRIMARY,
  },
  statusBadge: {
    display: 'inline-block',
    fontSize: 13,
    fontWeight: 600,
    padding: '3px 12px',
    borderRadius: 12,
  },
  btnGroup: {
    display: 'flex',
    gap: 10,
    padding: '0 16px 12px',
  },
  actionBtn: {
    flex: 1,
    padding: '13px 0',
    border: 'none',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  },
  updateBtn: {
    background: '#fff',
    color: PRIMARY,
    border: `2px solid ${PRIMARY}`,
  },
  submitBtn: {
    background: PRIMARY,
    color: '#fff',
  },
  deleteOrderBtn: {
    display: 'block',
    width: 'calc(100% - 32px)',
    margin: '4px 16px 24px',
    padding: '13px 0',
    background: '#fff',
    color: '#e53935',
    border: '2px solid #e53935',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'center',
  },
  toast: {
    position: 'fixed',
    bottom: 24,
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'rgba(0,0,0,0.75)',
    color: '#fff',
    padding: '8px 20px',
    borderRadius: 20,
    fontSize: 14,
    zIndex: 2000,
    whiteSpace: 'nowrap',
  },
  confirmOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 3000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  confirmBox: {
    background: '#fff',
    borderRadius: 12,
    padding: 24,
    width: 280,
    textAlign: 'center',
  },
  confirmTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: '#333',
    marginBottom: 8,
  },
  confirmMsg: {
    fontSize: 14,
    color: '#666',
    marginBottom: 20,
  },
  confirmBtns: {
    display: 'flex',
    gap: 10,
  },
  confirmCancel: {
    flex: 1,
    padding: '10px 0',
    background: '#f5f5f5',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    cursor: 'pointer',
    color: '#666',
  },
  confirmDelete: {
    flex: 1,
    padding: '10px 0',
    background: '#e53935',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  errorOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 4000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  errorBox: {
    background: '#fff',
    borderRadius: 12,
    padding: 20,
    maxWidth: 340,
    width: '100%',
    maxHeight: '70vh',
    overflow: 'auto',
  },
  errorTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: '#333',
    marginBottom: 12,
  },
  errorBody: {
    fontSize: 14,
    color: '#555',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  errorOk: {
    marginTop: 16,
    width: '100%',
    padding: '12px 0',
    background: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  },
};

/** 明細數量顯示用單位：散裝單位依登入語系；無 locales 時沿用後端 unit 快照。 */
function lineOrderQtyUnitLabel(line, t, uiLocale) {
  if (!line) return t('wpa.addToCart.defaultUnit');
  const fromLocales = getProductQtyStepUnitLabel(line, uiLocale, '');
  if (fromLocales) return fromLocales;
  const fromApi = line.unit != null && String(line.unit).trim() !== '' ? String(line.unit).trim() : '';
  if (fromApi) return fromApi;
  return t('wpa.addToCart.defaultUnit');
}

/** API 回傳 { success, data }；明細欄位為 camelCase 的 purchaseOrderNo、deliveryAddress、unitPrice 等 */
function normalizeOrderFromApi(json) {
  const raw = json?.data ?? json;
  if (!raw || typeof raw !== 'object') return null;
  if (raw.success === false && !raw.orderNo && !raw.id) return null;
  if (!raw.id && !raw.orderNo) return null;

  const lines = (raw.lines || raw.items || []).map((l) => ({
    id: l.id,
    productId: l.productId,
    itemNo: l.itemNo,
    productName: l.productName,
    name: l.productName,
    unitPrice: l.unitPrice != null ? Number(l.unitPrice) : 0,
    price: l.unitPrice != null ? Number(l.unitPrice) : 0,
    quantity: l.quantity != null ? Number(l.quantity) : 0,
    unit: l.unit,
    amount: l.amount != null ? Number(l.amount) : null,
    minOrderQuantity: l.minOrderQuantity != null ? Number(l.minOrderQuantity) : (l.min_order_quantity != null ? Number(l.min_order_quantity) : null),
    qtyPerUnit: l.qtyPerUnit != null ? Number(l.qtyPerUnit) : (l.qty_per_unit != null ? Number(l.qty_per_unit) : null),
    oqm: l.oqm ?? l.Oqm ?? null,
  }));

  let deliveryDate = raw.deliveryDate;
  if (deliveryDate && typeof deliveryDate === 'string') {
    deliveryDate = deliveryDate.slice(0, 10);
  }

  return {
    id: raw.id,
    orderNo: raw.orderNo,
    orderDate: raw.orderDate,
    status: raw.status,
    isReadBy3psys: raw.isReadBy3psys ?? false,
    customerId: raw.customerId ?? raw.CustomerId ?? null,
    customerNo: raw.customerNo ?? '',
    branchNo: raw.branchNo ?? '',
    deliveryDate: deliveryDate || '',
    poNumber: raw.purchaseOrderNo ?? raw.poNumber ?? '',
    remark: raw.remark ?? '',
    address: raw.deliveryAddress ?? raw.address ?? '',
    currency: raw.currency,
    totalAmount: raw.totalAmount != null ? Number(raw.totalAmount) : null,
    lines,
    items: lines,
  };
}

function buildUpdatePayload(order) {
  const lines = (order.lines || order.items || []).map((l) => ({
    productId: l.productId ?? null,
    itemNo: l.itemNo ?? '',
    productName: l.name || l.productName || '',
    unitPrice: Number(l.unitPrice ?? l.price ?? 0),
    quantity: Number(l.quantity ?? 0),
    unit: l.unit ?? null,
  }));

  return {
    deliveryDate: order.deliveryDate ? `${String(order.deliveryDate).slice(0, 10)}T12:00:00.000Z` : null,
    purchaseOrderNo: order.poNumber?.trim() || null,
    remark: order.remark?.trim() || null,
    deliveryAddress: order.address?.trim() || null,
    lines,
  };
}

/** 當訂單已被第三方讀入（isReadBy3psys）且仍為 submitted 時，視覺上呈現為 processing */
function resolveDisplayStatus(order) {
  if (!order) return '';
  const { status, isReadBy3psys } = order;
  if (status === 'submitted' && isReadBy3psys) return 'processing';
  return status || '';
}

function getStatusStyle(status) {
  switch (status) {
    case 'pending':
      return { background: '#fff3e0', color: '#f57c00' };
    case 'submitted':
      return { background: '#e3f2fd', color: '#1976d2' };
    case 'processing':
      return { background: '#f3e5f5', color: '#7b1fa2' };
    case 'completed':
      return { background: '#e8f5e9', color: '#388e3c' };
    case 'cancelled':
      return { background: '#fbe9e7', color: '#d32f2f' };
    default:
      return { background: '#f5f5f5', color: '#666' };
  }
}

export default function WpaOrderDetailPage() {
  const { t, currentLanguage } = useLanguage();
  const { companyId, orderId: id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [errorDialog, setErrorDialog] = useState(null);
  const antdLocale = useMemo(() => {
    switch (currentLanguage) {
      case 'zh-SC': return zhCN;
      case 'en': return enUS;
      default: return zhTW;
    }
  }, [currentLanguage]);
  const { disabledDate, getReason, loading: availabilityLoading, extendPrefetchTo } = useWpaDeliveryAvailability({
    customerId: order?.customerId,
    enabled: order != null,
  });
  const deliveryDateStr = order?.deliveryDate ? String(order.deliveryDate).slice(0, 10) : '';
  const deliveryDateDayjs = useMemo(
    () => (deliveryDateStr ? dayjs(deliveryDateStr) : null),
    [deliveryDateStr],
  );

  const statusLabel = useMemo(() => ({
    pending: t('wpa.orders.statusPending'),
    submitted: t('wpa.orders.statusSubmitted'),
    processing: t('wpa.orders.statusProcessing'),
    completed: t('wpa.orders.statusCompleted'),
    cancelled: t('wpa.orders.statusCancelled'),
  }), [t]);

  const displayStatus = resolveDisplayStatus(order);

  const isPending = order?.status === 'pending';

  const fetchOrder = useCallback(async () => {
    const token = localStorage.getItem('wpa_token');
    try {
      const res = await fetch(`/api/wpa-commerce/orders/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        navigate(`/${companyId}/wpa/login`, { replace: true });
        return;
      }
      const json = await res.json();
      if (!res.ok) {
        setOrder(null);
        return;
      }
      const normalized = normalizeOrderFromApi(json);
      setOrder(normalized);
    } catch (err) {
      console.error('Failed to fetch order', err);
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, [id, navigate, companyId]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2000);
  };

  const updateField = (field, value) => {
    setOrder((prev) => ({ ...prev, [field]: value }));
  };

  const updateLineQty = (idx, delta) => {
    setOrder((prev) => {
      const lines = [...(prev.lines || prev.items || [])];
      const prevLine = lines[idx];
      const line = { ...prevLine };
      const cur = Number(line.quantity || 0);
      if ((delta === 1 || delta === -1) && shouldLockWpaUnitStepOfOne(line)) {
        return prev;
      }
      line.quantity = Math.max(1, cur + delta);
      line.amount = undefined;
      lines[idx] = line;
      return { ...prev, lines, items: lines };
    });
  };

  const deleteLine = (idx) => {
    setOrder((prev) => {
      const lines = [...(prev.lines || prev.items || [])].filter((_, i) => i !== idx);
      return { ...prev, lines, items: lines };
    });
  };

  const calcTotal = () => {
    const lines = order?.lines || order?.items || [];
    if (lines.length === 0) {
      if (order?.totalAmount != null && !Number.isNaN(order.totalAmount)) return order.totalAmount;
      return 0;
    }
    return lines.reduce((sum, l) => {
      const up = Number(l.unitPrice ?? l.price ?? 0);
      const q = Number(l.quantity ?? 0);
      return sum + computeLineAmountFromRow(q, up, l);
    }, 0);
  };

  const handleUpdate = async () => {
    if (saving) return;
    setSaving(true);
    const token = localStorage.getItem('wpa_token');
    try {
      const res = await fetch(`/api/wpa-commerce/orders/${id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(buildUpdatePayload(order)),
      });
      if (res.status === 401) {
        navigate(`/${companyId}/wpa/login`, { replace: true });
        return;
      }
      if (res.ok) {
        showToast(t('wpa.orderDetail.orderUpdated'));
        fetchOrder();
      } else {
        const errJson = await res.json().catch(() => ({}));
        setErrorDialog(formatWpaCommerceApiError(res, errJson, t));
      }
    } catch {
      showToast(t('wpa.orderDetail.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (saving) return;
    const lines = order?.lines || order?.items || [];
    const moqMsg = buildMoqSubmitErrorMessage(collectMoqViolations(lines), t);
    if (moqMsg) {
      setErrorDialog(moqMsg);
      return;
    }
    setSaving(true);
    const token = localStorage.getItem('wpa_token');
    try {
      const putRes = await fetch(`/api/wpa-commerce/orders/${id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(buildUpdatePayload(order)),
      });
      if (putRes.status === 401) {
        navigate(`/${companyId}/wpa/login`, { replace: true });
        return;
      }
      if (!putRes.ok) {
        const errPut = await putRes.json().catch(() => ({}));
        setErrorDialog(formatWpaCommerceApiError(putRes, errPut, t));
        return;
      }

      const res = await fetch('/api/wpa-commerce/orders/submit', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderIds: [id] }),
      });
      if (res.status === 401) {
        navigate(`/${companyId}/wpa/login`, { replace: true });
        return;
      }
      if (res.ok) {
        showToast(t('wpa.orderDetail.orderSubmitted'));
        fetchOrder();
      } else {
        const errSub = await res.json().catch(() => ({}));
        setErrorDialog(formatWpaCommerceApiError(res, errSub, t));
      }
    } catch {
      showToast(t('wpa.orderDetail.submitFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setConfirmDelete(false);
    const token = localStorage.getItem('wpa_token');
    try {
      const res = await fetch(`/api/wpa-commerce/orders/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        navigate(`/${companyId}/wpa/login`, { replace: true });
        return;
      }
      if (res.ok) {
        showToast(t('wpa.orderDetail.orderDeleted'));
        setTimeout(() => navigate(`/${companyId}/wpa/orders`, { replace: true }), 1000);
      } else {
        showToast(t('wpa.orderDetail.deleteFailed'));
      }
    } catch {
      showToast(t('wpa.orderDetail.deleteFailed'));
    }
  };

  const layoutBack = {
    leadingMode: 'back',
    onBack: () => navigate(`/${companyId}/wpa/orders`),
  };

  if (loading) {
    return (
      <WpaLayout title={t('wpa.titles.orderDetail')} {...layoutBack}>
        <div style={styles.loading}>{t('wpa.orderDetail.loading')}</div>
      </WpaLayout>
    );
  }

  if (!order) {
    return (
      <WpaLayout title={t('wpa.titles.orderDetail')} {...layoutBack}>
        <div style={styles.loading}>{t('wpa.orderDetail.notFound')}</div>
      </WpaLayout>
    );
  }

  const lines = order.lines || order.items || [];
  const total = calcTotal();

  return (
    <WpaLayout title={t('wpa.orderDetail.titleWithNo', { orderNo: order.orderNo || id || '—' })} {...layoutBack}>
      <div style={styles.card}>
        <div style={styles.cardTitle}>{t('wpa.orderDetail.orderInfo')}</div>
        <div style={styles.fieldRow}>
          <span style={styles.fieldLabel}>{t('wpa.orderDetail.orderNo')}</span>
          <span style={styles.fieldValue}>{order.orderNo}</span>
        </div>
        <div style={styles.fieldRow}>
          <span style={styles.fieldLabel}>{t('wpa.orderDetail.status')}</span>
          <span style={{ ...styles.statusBadge, ...getStatusStyle(displayStatus) }}>
            {statusLabel[displayStatus] || order.status}
          </span>
        </div>
        <div style={styles.fieldRow}>
          <span style={styles.fieldLabel}>{t('wpa.orderDetail.orderDate')}</span>
          <span style={styles.fieldValue}>
            {order.orderDate
              ? formatWpaDate(order.orderDate, getWpaCompanyTimezone(), 'YYYY-MM-DD HH:mm')
              : '-'}
          </span>
        </div>
        <div style={styles.fieldRow}>
          <span style={styles.fieldLabel}>{t('wpa.orderDetail.deliveryDate')}</span>
          {isPending ? (
            <ConfigProvider locale={antdLocale}>
              <DatePicker
                style={{ width: 180 }}
                popupClassName="wpa-delivery-picker-popup"
                value={deliveryDateDayjs}
                format="YYYY-MM-DD"
                allowClear={false}
                inputReadOnly
                disabledDate={disabledDate}
                onChange={(d) => {
                  updateField('deliveryDate', d ? d.format('YYYY-MM-DD') : '');
                }}
                onPanelChange={(d) => {
                  if (d) extendPrefetchTo(d);
                }}
                placeholder={availabilityLoading ? '載入中…' : ''}
              />
            </ConfigProvider>
          ) : (
            <span style={styles.fieldValue}>{order.deliveryDate || '-'}</span>
          )}
        </div>
        {isPending && (() => {
          const reason = deliveryDateDayjs ? getReason(deliveryDateDayjs) : null;
          return reason ? (
            <div style={{ padding: '4px 0 8px', fontSize: 12, color: '#e65100' }}>{reason.message}</div>
          ) : null;
        })()}
        <div style={styles.fieldRow}>
          <span style={styles.fieldLabel}>{t('wpa.orderDetail.customerNo')}</span>
          <span style={styles.fieldValue}>
            {[order.customerNo, order.branchNo].filter(Boolean).join(' - ') || '-'}
          </span>
        </div>
        <div style={styles.fieldRowAddress}>
          <span style={{ ...styles.fieldLabel, paddingTop: 8 }}>{t('wpa.orderDetail.address')}</span>
          <span style={{ ...styles.fieldValue, whiteSpace: 'pre-line', textAlign: 'right', maxWidth: '65%' }}>
            {order.address?.trim() ? order.address : '-'}
          </span>
        </div>
        <div style={styles.fieldRow}>
          <span style={styles.fieldLabel}>{t('wpa.orderDetail.poNo')}</span>
          {isPending ? (
            <input
              style={styles.input}
              value={order.poNumber || ''}
              onChange={(e) => updateField('poNumber', e.target.value)}
              placeholder={t('wpa.orderDetail.poPlaceholder')}
            />
          ) : (
            <span style={styles.fieldValue}>{order.poNumber || '-'}</span>
          )}
        </div>
        <div style={styles.fieldRow}>
          <span style={styles.fieldLabel}>{t('wpa.orderDetail.remark')}</span>
          {isPending ? (
            <input
              style={styles.input}
              value={order.remark || ''}
              onChange={(e) => updateField('remark', e.target.value)}
              placeholder={t('wpa.orderDetail.remarkPlaceholder')}
            />
          ) : (
            <span style={styles.fieldValue}>{order.remark || '-'}</span>
          )}
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.cardTitle}>{t('wpa.orderDetail.orderLines')}</div>
        {lines.map((line, idx) => {
          const fastStep = resolveFastQtyStepSize(line) ?? 10;
          const stepLabel = formatQtyStepForDisplay(fastStep);
          const lockUnitStep = shouldLockWpaUnitStepOfOne(line);
          const oneBtn = (extra) => ({
            ...styles.qtyBtn,
            ...extra,
            ...(lockUnitStep ? { opacity: 0.45, cursor: 'not-allowed' } : {}),
          });
          const qtyUnitLabel = lineOrderQtyUnitLabel(line, t, currentLanguage);
          const qtyWithUom = t('wpa.addToCart.qtyWithUnitFmt', { qty: line.quantity, unit: qtyUnitLabel });
          return (
            <div key={line.id || idx} style={styles.lineItem}>
              <div style={styles.lineInfo}>
                <div style={styles.lineName}>{line.name || line.productName}</div>
                <div style={styles.lineItemNo}>{line.itemNo}</div>
                <div style={styles.linePrice} translate="no">
                  ${Number(line.unitPrice ?? line.price ?? 0).toFixed(2)}
                </div>
                <div style={styles.lineSubtotal} translate="no">
                  {t('wpa.orderDetail.lineSubtotalFmt', {
                    amount: `$${computeLineAmountFromRow(
                      Number(line.quantity ?? 0),
                      Number(line.unitPrice ?? line.price ?? 0),
                      line,
                    ).toFixed(2)}`,
                  })}
                </div>
              </div>
              {isPending ? (
                <>
                  <div style={styles.qtyRow}>
                    <button
                      type="button"
                      style={{ ...styles.qtyBtn, ...styles.qtyBtnWide }}
                      onClick={() => updateLineQty(idx, -fastStep)}
                    >
                      {t('wpa.addToCart.fastStepMinus', { step: stepLabel })}
                    </button>
                    <button
                      type="button"
                      style={oneBtn({})}
                      disabled={lockUnitStep}
                      aria-disabled={lockUnitStep}
                      onClick={() => { if (!lockUnitStep) updateLineQty(idx, -1); }}
                    >
                      −
                    </button>
                    <span style={styles.qtyText}>{qtyWithUom}</span>
                    <button
                      type="button"
                      style={oneBtn({})}
                      disabled={lockUnitStep}
                      aria-disabled={lockUnitStep}
                      onClick={() => { if (!lockUnitStep) updateLineQty(idx, 1); }}
                    >
                      +
                    </button>
                    <button
                      type="button"
                      style={{ ...styles.qtyBtn, ...styles.qtyBtnWide }}
                      onClick={() => updateLineQty(idx, fastStep)}
                    >
                      {t('wpa.addToCart.fastStepPlus', { step: stepLabel })}
                    </button>
                  </div>
                  <button
                    type="button"
                    style={styles.lineDeleteBtn}
                    onClick={() => deleteLine(idx)}
                    aria-label={t('wpa.orderDetail.deleteLineAria')}
                  >
                    ✕
                  </button>
                </>
              ) : (
                <span style={styles.qtyText}>{qtyWithUom}</span>
              )}
            </div>
          );
        })}
        <div style={styles.totalRow}>
          <span style={styles.totalLabel}>{t('wpa.orderDetail.total')}</span>
          <span style={styles.totalValue} translate="no">${total.toFixed(2)}</span>
        </div>
      </div>

      {isPending && (
        <>
          <div style={styles.btnGroup}>
            <button
              style={{ ...styles.actionBtn, ...styles.updateBtn }}
              onClick={handleUpdate}
              disabled={saving}
            >
              {saving ? t('wpa.orderDetail.processing') : t('wpa.orderDetail.updateOrder')}
            </button>
            <button
              style={{ ...styles.actionBtn, ...styles.submitBtn }}
              onClick={handleSubmit}
              disabled={saving}
            >
              {t('wpa.orderDetail.submitOrder')}
            </button>
          </div>
          <button
            style={styles.deleteOrderBtn}
            onClick={() => setConfirmDelete(true)}
          >
            {t('wpa.orderDetail.deleteOrder')}
          </button>
        </>
      )}

      <div style={{ height: 24 }} />

      {confirmDelete && (
        <div style={styles.confirmOverlay}>
          <div style={styles.confirmBox}>
            <div style={styles.confirmTitle}>{t('wpa.orderDetail.confirmDelete')}</div>
            <div style={styles.confirmMsg}>{t('wpa.orderDetail.confirmDeleteMsg')}</div>
            <div style={styles.confirmBtns}>
              <button style={styles.confirmCancel} onClick={() => setConfirmDelete(false)}>
                {t('wpa.orderDetail.cancel')}
              </button>
              <button style={styles.confirmDelete} onClick={handleDelete}>
                {t('wpa.orderDetail.confirmDeleteBtn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div style={styles.toast}>{toast}</div>}

      {errorDialog && (
        <div style={styles.errorOverlay} role="dialog" aria-modal="true">
          <div style={styles.errorBox}>
            <div style={styles.errorTitle}>{t('wpa.orderDetail.errorTitle')}</div>
            <div style={styles.errorBody}>{errorDialog}</div>
            <button type="button" style={styles.errorOk} onClick={() => setErrorDialog(null)}>
              {t('wpa.orderDetail.errorOk')}
            </button>
          </div>
        </div>
      )}
    </WpaLayout>
  );
}
